/**
 * Radio — 14 px circular indicator with optional inline label.
 * Native input is visually hidden; the visual indicator is a `<span>` driven
 * by `peer-checked`. Use the standard `name` prop to group radios.
 *
 * Not present in the form artboards — extrapolated within the language:
 * 1 px line border, ink ring + ink center dot on checked.
 */

import type { InputHTMLAttributes, ReactNode, Ref } from 'react';

interface RadioProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  ref?: Ref<HTMLInputElement>;
}

export function Radio({ label, className = '', disabled, ref, ...rest }: RadioProps) {
  const wrapper = label
    ? `inline-flex items-center gap-2.5 ${disabled ? 'opacity-60' : 'cursor-pointer'}`
    : 'inline-flex';

  return (
    <label className={`${wrapper} ${className}`}>
      <input
        {...rest}
        ref={ref}
        type="radio"
        disabled={disabled}
        className="peer sr-only"
      />
      <span
        aria-hidden
        className="
          grid size-[14px] flex-shrink-0 place-items-center rounded-full border border-line bg-paper
          transition-colors
          peer-hover:border-ink
          peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-accent peer-focus-visible:outline-offset-2
          peer-checked:border-ink
          peer-checked:[&>span]:scale-100
        "
      >
        <span className="size-1.5 scale-0 rounded-full bg-ink transition-transform" />
      </span>
      {label && <span className="text-[13px] leading-tight text-ink">{label}</span>}
    </label>
  );
}
